/**
 * usePublicChatController — chat support pour VISITEUR ANONYME (site public donoor.org).
 *
 * Aucune authentification : l'identité = un `visitor_id` (nanoid) généré au
 * premier usage et persisté en localStorage (stable entre sessions et onglets,
 * partagé avec le dashboard via le domaine racine .donoor.org pour le
 * rattachement à l'inscription). Toutes les requêtes passent par les endpoints
 * publics /api/public/chat/* de l'API (transport : `publicChatApi`, ADR-001 ;
 * cf. publicChatRoutes.ts) et par le salon socket anonyme
 * `chat:visitor:{visitorId}`.
 *
 * Volontairement plus simple que le widget dashboard (pas de pièces jointes,
 * pas d'accusés lu/distribué) : un visiteur veut juste poser une question et
 * recevoir la réponse de l'équipe en direct.
 *
 * Assistant IA (migration 330) : tant que `iaActive` est vrai, c'est l'IA qui
 * répond ; « Parler à l'équipe » (ou une réponse d'un membre) passe la main à
 * l'équipe. Les questions toutes prêtes ont une réponse écrite à l'avance.
 */
import { ref, computed } from "vue";
import { nanoid } from "nanoid";
import type { Socket } from "socket.io-client";
import type { ChatMessage, ChatConversation } from "@donoor/api-contract";
import { publicChatApi, type AssistantPublic } from "~/services/api/publicChat";
import { LANGUES, COOKIE_LANGUE, type Langue } from "~/utils/langue";
import { useChatPageContext } from "~/composables/useChatPageContext";

const VISITOR_ID_KEY = "donoor_visitor_id";
const CONV_ID_KEY = "donoor_visitor_conv_id";

/** Récupère (ou crée) l'id visiteur persistant. */
function getOrCreateVisitorId(): string {
  if (import.meta.server) return "";
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id || !/^[A-Za-z0-9_-]{16,40}$/.test(id)) {
    id = nanoid(24); // 24 chars, dans la fenêtre [16,40] du backend
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
}

/**
 * Langue du contenu IA du chat (accueil, questions toutes prêtes) : choix
 * explicite du sélecteur de langue du site (cookie) sinon langue du
 * navigateur, sinon français — même priorité que `langue-navigateur.client.ts`.
 *
 * INDÉPENDANTE de la langue des PAGES (chantier multilingue 2026-10-10, pas
 * encore ouvert partout) : le widget flotte sur toutes les pages, traduites
 * ou non, donc le chat suit le visiteur même sur une page restée en français.
 */
function langueChat(): Langue {
  if (import.meta.server) return "fr";
  const choix = useCookie<Langue | null>(COOKIE_LANGUE).value;
  if (choix && (LANGUES as readonly string[]).includes(choix)) return choix;
  const langues = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const l of langues) {
    const code = (l ?? "").toLowerCase().split("-")[0];
    const trouvee = LANGUES.find((candidate) => candidate === code);
    if (trouvee) return trouvee;
  }
  return "fr";
}

export function usePublicChatController() {
  const config = useRuntimeConfig();
  const apiBase = config.public.apiBaseUrl as string;
  const { contexte: contextePage } = useChatPageContext();

  const visitorId = ref("");
  const conversationId = ref<number | null>(null);
  const messages = ref<ChatMessage[]>([]);
  const sending = ref(false);
  const loading = ref(false);
  const unread = ref(0);
  const open = ref(false);
  // Parité chat dashboard :
  const staffOnline = ref(false); // au moins un membre équipe connecté
  const staffTyping = ref(false); // un membre est en train d'écrire
  const staffReadAt = ref<string | null>(null); // jusqu'où l'équipe a lu (✓✓)
  const assignedName = ref<string | null>(null); // membre qui répond
  const assignedAvatar = ref<string | null>(null);
  // Assistant IA.
  const assistant = ref<AssistantPublic | null>(null);
  const iaActive = ref(false); // l'IA a la main dans la conversation en cours
  const aiTyping = ref(false); // « L'assistant écrit… »
  const veutEquipe = ref(false); // « Parler à l'équipe » cliqué avant le 1er message
  let socket: Socket | null = null;
  let staffTypingTimer: ReturnType<typeof setTimeout> | null = null;
  let aiTypingTimer: ReturnType<typeof setTimeout> | null = null;
  let myTypingTimer: ReturnType<typeof setTimeout> | null = null;
  let lastTypingSent = 0;

  const hasConversation = computed(() => conversationId.value != null);

  /** C'est l'équipe (et non l'IA) qui répond à ce qui sera écrit maintenant. */
  const modeEquipe = computed(() =>
    hasConversation.value
      ? !iaActive.value
      : veutEquipe.value || !assistant.value?.actif,
  );

  /** L'IA prépare une réponse. Filet de 40 s si l'événement de fin se perd. */
  function setAiTyping(valeur: boolean): void {
    aiTyping.value = valeur;
    if (aiTypingTimer) clearTimeout(aiTypingTimer);
    if (valeur) aiTypingTimer = setTimeout(() => (aiTyping.value = false), 40_000);
  }

  /** Ajoute un message au fil sauf s'il y est déjà (socket + rechargement). */
  function ajouterMessage(m: ChatMessage): void {
    if (messages.value.some((x) => x.id === m.id)) return;
    messages.value = [...messages.value, m];
  }

  /** Page actuelle du site, transmise à l'assistant (chemin seul). */
  function pageCourante(): string {
    return import.meta.server ? "/" : window.location.pathname;
  }

  /**
   * Exécute un appel `publicChatApi`. Ne lève jamais : renvoie null en cas
   * d'échec (le widget ne doit jamais casser la page hôte).
   */
  async function sansErreur<T>(appel: () => Promise<T>): Promise<T | null> {
    try {
      return await appel();
    } catch {
      return null;
    }
  }

  /** Initialise l'identité + reprend une conversation existante (localStorage). */
  function init(): void {
    if (import.meta.server) return;
    visitorId.value = getOrCreateVisitorId();
    const savedConv = localStorage.getItem(CONV_ID_KEY);
    if (savedConv) conversationId.value = Number(savedConv);
  }

  /** Connexion socket + abonnement salon visiteur + salon conversation (parité). */
  async function connectSocket(): Promise<void> {
    if (socket || import.meta.server || !visitorId.value) return;
    const { io } = await import("socket.io-client");
    socket = io(apiBase, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      reconnection: true,
    });
    socket.on("connect", () => {
      socket?.emit("join:chat-visitor", {
        visitorId: visitorId.value,
        conversationId: conversationId.value ?? undefined,
      });
    });

    // Nouveau message (réponse staff).
    socket.on(
      "chat:message",
      (payload: { conversationId: number; message: ChatMessage }) => {
        if (payload.conversationId !== conversationId.value) return;
        if (payload.message.sender_type === "visitor") return; // écho
        ajouterMessage(payload.message);
        staffTyping.value = false;
        if (payload.message.sender_type === "ai") setAiTyping(false);
        if (!open.value) unread.value += 1;
        if (open.value) void markRead();
      },
    );

    // Présence équipe (émis globalement par le serveur).
    socket.on("chat:presence", (payload: { staffOnline: boolean }) => {
      staffOnline.value = Boolean(payload.staffOnline);
    });

    // « En train d'écrire… » — l'équipe ou l'assistant IA.
    socket.on(
      "chat:typing",
      (payload: { conversationId: number; senderType: string; isTyping: boolean }) => {
        if (payload.conversationId !== conversationId.value) return;
        if (payload.senderType === "ai") {
          setAiTyping(Boolean(payload.isTyping));
          return;
        }
        if (payload.senderType !== "staff") return;
        staffTyping.value = Boolean(payload.isTyping);
        if (staffTypingTimer) clearTimeout(staffTypingTimer);
        if (payload.isTyping) {
          // Filet : efface l'indicateur si aucun refresh sous 5s.
          staffTypingTimer = setTimeout(() => (staffTyping.value = false), 5000);
        }
      },
    );

    // Accusé de lecture équipe (✓✓) : le staff a lu jusqu'à `upToAt`.
    socket.on(
      "chat:read",
      (payload: { conversationId: number; readBy?: string; upToAt?: string }) => {
        if (payload.conversationId !== conversationId.value) return;
        if (payload.readBy === "staff" && payload.upToAt) {
          staffReadAt.value = payload.upToAt;
        }
      },
    );

    // Assignation / changement de conversation → met à jour qui répond.
    socket.on(
      "chat:conversation_updated",
      (payload: { conversation?: ChatConversation }) => {
        const c = payload?.conversation;
        if (!c || Number(c.id) !== conversationId.value) return;
        assignedName.value = c.assigned_to_name ?? null;
        assignedAvatar.value = c.assigned_to_avatar ?? null;
        iaActive.value = Boolean(c.ia_active);
        if (!c.ia_active) setAiTyping(false);
      },
    );
  }

  /** Émet l'indicateur « le visiteur écrit » (throttlé 2s). senderType 'org'
   *  = le camp non-staff côté modèle bicaméral. */
  function notifyTyping(): void {
    if (!socket || !conversationId.value) return;
    const now = Date.now();
    if (now - lastTypingSent > 2000) {
      lastTypingSent = now;
      socket.emit("chat:typing", {
        conversationId: conversationId.value,
        senderType: "org",
        isTyping: true,
      });
    }
    if (myTypingTimer) clearTimeout(myTypingTimer);
    myTypingTimer = setTimeout(() => {
      socket?.emit("chat:typing", {
        conversationId: conversationId.value,
        senderType: "org",
        isTyping: false,
      });
    }, 2500);
  }

  /** Charge le fil de la conversation courante. */
  async function loadMessages(): Promise<void> {
    if (!conversationId.value || !visitorId.value) return;
    loading.value = true;
    const convId = conversationId.value;
    const res = await sansErreur(() =>
      publicChatApi.listMessages(convId, visitorId.value),
    );
    if (res?.data) messages.value = res.data;
    loading.value = false;
  }

  /** Marque le fil comme lu (curseur + reset badge local). */
  async function markRead(): Promise<void> {
    unread.value = 0;
    if (!conversationId.value || !visitorId.value) return;
    const convId = conversationId.value;
    await sansErreur(() => publicChatApi.markRead(convId, visitorId.value));
  }

  /**
   * Envoie un message. Ouvre la conversation au premier message.
   * `meta` = nom/email optionnels (formulaire d'entrée).
   */
  async function send(
    text: string,
    meta?: { name?: string; email?: string },
  ): Promise<void> {
    const body = text.trim();
    if (!body || sending.value || !visitorId.value) return;
    sending.value = true;

    // Ajout optimiste immédiat (UX instantanée).
    const optimistic: ChatMessage = {
      id: -Date.now(),
      conversation_id: conversationId.value ?? 0,
      sender_type: "visitor",
      sender_user_id: visitorId.value,
      sender_name: meta?.name ?? null,
      sender_avatar: null,
      body,
      attachments: [],
      edited_at: null,
      deleted_at: null,
      created_at: new Date().toISOString(),
    };
    messages.value = [...messages.value, optimistic];

    if (!conversationId.value) {
      // Première prise de contact → ouvre le fil (ou y revient, s'il
      // existait déjà côté serveur).
      const res = await sansErreur(() =>
        publicChatApi.startConversation({
          visitorId: visitorId.value,
          message: body,
          page: pageCourante(),
          pageContexte: contextePage.value?.resume,
          equipe: veutEquipe.value || undefined,
          ...meta,
        }),
      );
      if (res?.data?.conversation) {
        conversationId.value = Number(res.data.conversation.id);
        localStorage.setItem(CONV_ID_KEY, String(conversationId.value));
        iaActive.value = Boolean(res.data.conversation.ia_active);
        // L'IA répond tout de suite : son indicateur peut partir avant que le
        // socket soit connecté, on l'affiche donc d'office.
        if (iaActive.value) setAiTyping(true);
        // Remplace l'optimiste par le message réel + connecte le socket.
        messages.value = [res.data.message];
        await connectSocket();
        // Une réponse arrivée avant la connexion du socket, ou un fil qui
        // existait déjà (un seul fil par visiteur, son historique revient) :
        // on recharge le fil.
        void loadMessages();
      }
    } else {
      const convId = conversationId.value;
      if (iaActive.value) setAiTyping(true);
      const res = await sansErreur(() =>
        publicChatApi.sendMessage(convId, {
          visitorId: visitorId.value,
          message: body,
          page: pageCourante(),
          pageContexte: contextePage.value?.resume,
          ...meta,
        }),
      );
      if (res?.data) {
        // Remplace l'optimiste par la version serveur (id réel).
        messages.value = messages.value.map((m) =>
          m.id === optimistic.id ? res.data : m,
        );
      }
    }
    sending.value = false;
  }

  /**
   * Oublie la conversation en cours (ID introuvable côté serveur — supprimée,
   * ou localStorage d'un ancien test) : le widget revient à l'accueil de
   * l'assistant plutôt que de rester bloqué sur un panneau vide.
   */
  function resetConversation(): void {
    conversationId.value = null;
    messages.value = [];
    iaActive.value = false;
    assignedName.value = null;
    assignedAvatar.value = null;
    staffReadAt.value = null;
    veutEquipe.value = false;
    if (!import.meta.server) localStorage.removeItem(CONV_ID_KEY);
  }

  /** Charge les métadonnées de la conv courante (membre assigné, curseur lu
   *  équipe) depuis la liste des convs du visiteur. Un visiteur n'a qu'UN fil
   *  (migration 335, ses anciens fils y ont été fusionnés) : si celui retenu
   *  ici n'existe plus, on reprend ce fil-là ; s'il n'y en a aucun, on repart
   *  de zéro. */
  async function loadConversationMeta(): Promise<void> {
    if (!conversationId.value || !visitorId.value) return;
    const res = await sansErreur(() =>
      publicChatApi.listConversations(visitorId.value),
    );
    const conv =
      res?.data?.find((c) => Number(c.id) === conversationId.value) ?? res?.data?.[0];
    if (conv && Number(conv.id) !== conversationId.value) {
      conversationId.value = Number(conv.id);
      localStorage.setItem(CONV_ID_KEY, String(conversationId.value));
      await loadMessages();
    }
    if (conv) {
      assignedName.value = conv.assigned_to_name ?? null;
      assignedAvatar.value = conv.assigned_to_avatar ?? null;
      staffReadAt.value = conv.peer_last_read_at ?? null;
      iaActive.value = Boolean(conv.ia_active);
    } else if (res?.data) {
      // L'appel a réussi mais cette conversation n'existe plus pour ce visiteur.
      resetConversation();
    }
  }

  /**
   * Accueil et questions toutes prêtes de l'assistant — adaptés à la fiche
   * affichée (cagnotte, adhésion…) via `useChatPageContext`. Recharge dès que
   * le type de fiche ou la langue change (navigation en SPA : le widget,
   * monté une seule fois pour tout le site, ne se démonte jamais entre deux
   * pages) ; mis en cache sinon.
   */
  let dernierAssistantCharge: string | null = null;
  async function loadAssistant(): Promise<void> {
    const type = contextePage.value?.type ?? null;
    const langue = langueChat();
    const cle = `${type ?? ""}:${langue}`;
    if (assistant.value && dernierAssistantCharge === cle) return;
    const res = await sansErreur(() => publicChatApi.assistant(langue, type ?? undefined));
    if (res?.data) {
      assistant.value = res.data;
      dernierAssistantCharge = cle;
    }
  }

  /**
   * Question suggérée. Deux cas :
   *  - fiche précise affichée (`contextuel`) : pas de réponse pré-écrite, la
   *    question part comme un vrai message (passe par le modèle, avec le
   *    contexte de la page) ;
   *  - générique : réponse écrite à l'avance, 0 €.
   */
  async function askSuggestion(suggestionId: string): Promise<void> {
    if (sending.value || !visitorId.value) return;
    if (assistant.value?.contextuel) {
      const s = assistant.value.suggestions.find((q) => q.id === suggestionId);
      if (s) await send(s.question);
      return;
    }
    sending.value = true;
    const res = await sansErreur(() =>
      publicChatApi.askSuggestion(suggestionId, {
        visitorId: visitorId.value,
        conversationId: conversationId.value ?? undefined,
        langue: langueChat(),
      }),
    );
    const conv = res?.data?.conversation;
    if (conv) {
      const nouvelle = conversationId.value == null;
      conversationId.value = Number(conv.id);
      localStorage.setItem(CONV_ID_KEY, String(conversationId.value));
      iaActive.value = Boolean(conv.ia_active);
      await loadMessages();
      if (nouvelle) await connectSocket();
    }
    sending.value = false;
  }

  /**
   * « Parler à l'équipe ». Sans conversation : le premier message ira
   * directement à l'équipe. Avec : l'assistant laisse la main tout de suite.
   */
  async function askTeam(meta?: { name?: string; email?: string }): Promise<void> {
    veutEquipe.value = true;
    if (!conversationId.value || !visitorId.value || !iaActive.value) return;
    const convId = conversationId.value;
    const res = await sansErreur(() =>
      publicChatApi.askTeam(convId, { visitorId: visitorId.value, ...meta }),
    );
    if (res?.data) {
      iaActive.value = Boolean(res.data.ia_active);
      setAiTyping(false);
      await loadMessages();
    }
  }

  /** Ouvre le panneau : charge le fil + méta, connecte le socket, marque lu. */
  async function openPanel(): Promise<void> {
    open.value = true;
    unread.value = 0;
    void loadAssistant();
    if (conversationId.value) {
      await Promise.all([loadMessages(), loadConversationMeta()]);
      // Conversation introuvable (supprimée) : resetConversation() a vidé
      // conversationId pendant l'attente ci-dessus → pas de socket à connecter.
      if (!conversationId.value) return;
      await connectSocket();
      await markRead();
    }
  }

  function closePanel(): void {
    open.value = false;
  }

  function dispose(): void {
    if (staffTypingTimer) clearTimeout(staffTypingTimer);
    if (myTypingTimer) clearTimeout(myTypingTimer);
    if (aiTypingTimer) clearTimeout(aiTypingTimer);
    socket?.disconnect();
    socket = null;
  }

  return {
    visitorId,
    conversationId,
    messages,
    sending,
    loading,
    unread,
    open,
    staffOnline,
    staffTyping,
    staffReadAt,
    assignedName,
    assignedAvatar,
    hasConversation,
    assistant,
    iaActive,
    aiTyping,
    modeEquipe,
    askSuggestion,
    askTeam,
    init,
    openPanel,
    closePanel,
    send,
    notifyTyping,
    loadMessages,
    dispose,
  };
}
